import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../contexts/LanguageContext';
import { getProxyImageUrl } from '../../utils';
import { Badge } from '../ui/badge';

/**
 * Extract friendly rarity code from DB value (e.g. '500_rr' → 'RR', '600_r' → 'R')
 */
function formatRarity(rarity) {
  if (!rarity) return null;
  const value = typeof rarity === 'string' ? rarity : rarity?.value;
  if (typeof value !== 'string') return null;
  const parts = value.split('_');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : value.toUpperCase();
}

/**
 * Sci-fi teleportation loading animation — horizontal scan bars
 * that sweep across the card area as if materializing the image.
 */
function TeleportLoader() {
  return (
    <div className="teleport-loader">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="teleport-bar" style={{ animationDelay: `${i * 0.07}s` }} />
      ))}
      <div className="teleport-glow" />
    </div>
  );
}

/**
 * CardPreview - Shows an enlarged card preview on hover.
 * Uses a fixed-height container so utility tiles below don't shift.
 * Displays a sci-fi teleportation animation while the image loads.
 */
export default function CardPreview({ card, className }) {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [displayedSrc, setDisplayedSrc] = useState(null);
  const currentUrlRef = useRef(null);

  const imageUrl = card
    ? getProxyImageUrl(card.images?.blob?.full || card.images?.original?.full)
    : null;

  // When the card/url changes, mark as loading and start preloading
  useEffect(() => {
    if (!imageUrl) {
      setImageLoaded(false);
      setDisplayedSrc(null);
      currentUrlRef.current = null;
      return;
    }
    if (imageUrl === currentUrlRef.current) return; // same image, skip
    currentUrlRef.current = imageUrl;
    setImageLoaded(false);

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      // Only apply if this is still the image we want
      if (currentUrlRef.current === imageUrl) {
        setDisplayedSrc(imageUrl);
        setImageLoaded(true);
      }
    };
    img.onerror = () => {
      if (currentUrlRef.current === imageUrl) {
        setDisplayedSrc(imageUrl); // show broken image rather than infinite loader
        setImageLoaded(true);
      }
    };
  }, [imageUrl]);

  const rarityDisplay = card ? formatRarity(card.rarity) : null;
  const typeText = card ? (typeof card.type === 'string' ? card.type : card.type?.value || '') : '';

  return (
    <div
      className={`card-preview-container rounded-lg border bg-muted/30 overflow-hidden ${className || ''}`}
      style={{ minHeight: 520, maxHeight: 560 }}
    >
      {!card ? (
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm text-center p-4 border-dashed border border-muted-foreground/20 rounded-lg" style={{ minHeight: 520 }}>
          {t('preview.hoverHint', 'Hover over a card to preview')}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 p-3">
          <div className="teleport-image-wrapper aspect-[220/305] w-full relative rounded-lg overflow-hidden">
            {/* Teleport loader visible while image hasn't loaded */}
            {!imageLoaded && <TeleportLoader />}
            {/* Actual image — hidden until loaded, then fades in */}
            {displayedSrc && (
              <img
                src={displayedSrc}
                alt={card.name}
                className={`card-preview-image rounded-lg shadow-md w-full h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              />
            )}
          </div>
          <div className="card-preview-info text-center space-y-1.5 w-full px-1">
            <div className="font-semibold text-sm truncate">{card.name}</div>
            {card.number && <div className="text-xs text-muted-foreground">{card.number}</div>}
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
              {typeText && (
                <Badge className="text-[10px] px-2 py-0.5 uppercase tracking-wider pointer-events-none" style={{ background: 'var(--brand)', color: 'white', border: 'none' }}>
                  {typeText}
                </Badge>
              )}
              {rarityDisplay && (
                <Badge className="text-[10px] px-2 py-0.5 uppercase tracking-wider pointer-events-none" style={{ background: 'var(--brand-subtle)', color: 'white', border: 'none' }}>
                  {rarityDisplay}
                </Badge>
              )}
              {card.level != null && (
                <Badge className="text-[10px] px-2 py-0.5 uppercase tracking-wider pointer-events-none" style={{ background: 'var(--brand-accent)', color: 'white', border: 'none' }}>
                  Level {card.level}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
